/**
 * Colour conversion, by hand.
 *
 * `culori` is in package.json and unused. It is an excellent library aimed at
 * perceptual spaces — OKLCH, colour differencing, gamut mapping — which is what
 * the design tokens need and not what a picker needs. A picker needs hex ↔ HSV
 * and nothing else, that is forty lines, and this file is imported by the entry
 * chunk where a colour library is not worth its bytes.
 *
 * HSV rather than HSL because a saturation/value square is the control people
 * expect: one axis to white, one to black. HSL's lightness axis puts pure hues
 * in the middle, so dragging to a corner does not give you white or black and
 * the square feels wrong in a way that is hard to articulate and easy to notice.
 */

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export interface Hsv {
  /** 0–360 */
  h: number;
  /** 0–1 */
  s: number;
  /** 0–1 */
  v: number;
}

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

/**
 * Parse anything a person might type.
 *
 * Accepts `#abc`, `abc`, `#aabbcc`, `AABBCC`, with surrounding whitespace.
 * Returns null rather than a fallback colour — a silent default here means a
 * typo silently paints the wrong colour, which is worse than a field that
 * refuses to commit.
 */
export function parseHex(input: string): Rgb | null {
  const s = input.trim().replace(/^#/, "");
  if (!/^[0-9a-fA-F]+$/.test(s)) return null;

  if (s.length === 3) {
    // #abc means #aabbcc — each digit doubled, not padded with zeros.
    const r = Number.parseInt(s[0] as string, 16);
    const g = Number.parseInt(s[1] as string, 16);
    const b = Number.parseInt(s[2] as string, 16);
    return { r: r * 17, g: g * 17, b: b * 17 };
  }
  if (s.length === 6) {
    return {
      r: Number.parseInt(s.slice(0, 2), 16),
      g: Number.parseInt(s.slice(2, 4), 16),
      b: Number.parseInt(s.slice(4, 6), 16),
    };
  }
  return null;
}

export function toHex({ r, g, b }: Rgb): string {
  const part = (n: number) => clamp(Math.round(n), 0, 255).toString(16).padStart(2, "0");
  return `#${part(r)}${part(g)}${part(b)}`.toUpperCase();
}

export function rgbToHsv({ r, g, b }: Rgb): Hsv {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;

  let h = 0;
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }

  return { h, s: max === 0 ? 0 : d / max, v: max };
}

export function hsvToRgb({ h, s, v }: Hsv): Rgb {
  const hh = ((h % 360) + 360) % 360;
  const sn = clamp(s, 0, 1);
  const vn = clamp(v, 0, 1);

  const c = vn * sn;
  const x = c * (1 - Math.abs(((hh / 60) % 2) - 1));
  const m = vn - c;

  const seg = Math.floor(hh / 60) % 6;
  const [r1, g1, b1] = (
    [
      [c, x, 0],
      [x, c, 0],
      [0, c, x],
      [0, x, c],
      [x, 0, c],
      [c, 0, x],
    ] as const
  )[seg] as readonly [number, number, number];

  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  };
}

export const hexToHsv = (hex: string): Hsv | null => {
  const rgb = parseHex(hex);
  return rgb ? rgbToHsv(rgb) : null;
};

export const hsvToHex = (hsv: Hsv): string => toHex(hsvToRgb(hsv));

/**
 * Black or white text, whichever is readable on this background.
 *
 * Relative luminance per WCAG, not a naive average: the eye is far more
 * sensitive to green than to blue, so `(r+g+b)/3` calls pure blue "light" and
 * puts black text on it.
 */
export function readableTextOn(rgb: Rgb): "#000000" | "#FFFFFF" {
  const channel = (n: number) => {
    const c = n / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const l = 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
  return l > 0.179 ? "#000000" : "#FFFFFF";
}
